import { captureException } from '@sentry/react';
import { StateCreator } from 'zustand';

import { roleService } from '@/services/database';
import {
    appendEntity,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
} from '@/stores/helpers/collections';
import { Role } from '@/types';

import { RoleActions, RoleState } from '../types';

export const createRoleActions: StateCreator<RoleState & RoleActions, [['zustand/immer', never]], [], RoleActions> = (
    set,
    get,
) => ({
    // Core
    fetchRoles: async (params) => {
        set({ loading: true });

        try {
            const roles = await roleService.list(params);

            set({ roles, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load roles' });
        }
    },
    fetchRoleById: async (id) => {
        set({ loadingRole: true, error: undefined });

        try {
            const role = await roleService.getById(id);

            set({ selectedRole: role, loadingRole: false });
        } catch (error) {
            captureException(error);

            set({ error: 'Failed to fetch role detail', loadingRole: false });
        }
    },
    clearSelectedRole: () => {
        set({ selectedRole: null });
    },
    createRole: async (input) => {
        try {
            const role = await roleService.create(input);

            set({ roles: appendEntity(get().roles, role) });

            return role;
        } catch (error) {
            captureException(error);

            set({ error: 'Failed to create role' });
        }
    },
    updateRole: async (id, updates) => {
        const prev = get().roles.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        const optimisticUpdates: Partial<Role> = {
            ...(updates.name !== undefined ? { name: updates.name } : {}),
        };

        set({
            roles: mergeEntityById(get().roles, id, optimisticUpdates),
            selectedRole:
                get().selectedRole?.id === id
                    ? mergeEntity(get().selectedRole!, optimisticUpdates)
                    : get().selectedRole,
        });

        try {
            const role = await roleService.update(id, updates);

            set({
                roles: mergeEntityById(get().roles, id, role),
                selectedRole:
                    get().selectedRole?.id === id ? mergeEntity(get().selectedRole!, role) : get().selectedRole,
            });
        } catch (error) {
            captureException(error);

            set({
                roles: replaceEntityById(get().roles, id, prev),
                selectedRole: get().selectedRole?.id === id ? prev : get().selectedRole,
                error: 'Failed to update role',
            });
            throw error;
        }
    },
    deleteRole: async (id) => {
        const prev = get().roles;

        set({
            roles: removeEntityById(get().roles, id),
            selectedRole: get().selectedRole?.id === id ? null : get().selectedRole,
        });

        try {
            await roleService.delete(id);
        } catch (error) {
            captureException(error);

            set({ roles: prev, error: 'Failed to delete role' });
            throw error;
        }
    },
});
